import React, { useRef } from "react";
import { useHistory } from "react-router";
import { IonButton, IonInput, IonSpinner, useIonToast } from "@ionic/react";
import { useAuthBySms } from "../../../../providers/Auth";
import { useHandleCustomerLogin } from "../../../../hooks/useHandleCustomerLogin";
import { Controller, FieldValues, useForm } from "react-hook-form";
import { VerificationDateType } from "../types";
import CommonFormElement from "../../../../components/UI/CommonFormElements/CommonFormElement";
import { validateInputToNumberType } from "../utils";
import useCheckWasInvitedMutation from "../../../../providers/referrals/useCheckWasInvitedMutation";
import { useTranslation } from "react-i18next";
import ResendOtpSms from "./ResendOtpSms";
import ResendCallCode from "./ResendCallCode";
import { useReferralStore } from "../../../../providers/referrals/useReferralStore";
import { toast } from "react-toastify";

interface CustomLocationState {
  from?: string;
}

type OtpCodeInputFormProps = {
  verificationData: VerificationDateType;
  setVerificationData: (data: VerificationDateType) => void;
};

const OtpCodeInputForm = ({ verificationData, setVerificationData }: OtpCodeInputFormProps) => {
  const { t } = useTranslation();
  const phoneNumber = verificationData.phoneNumber;
  const [present] = useIonToast();
  const history = useHistory<CustomLocationState>();
  const handleCustomerLogin = useHandleCustomerLogin();
  const { mutate: mutateAuth, isLoading: isAuthLoading, isError, isSuccess } = useAuthBySms();
  const { mutateAsync: checkWasInvitedMutationAsync, isLoading: isCheckWasInvitedLoading } =
    useCheckWasInvitedMutation();
  const inputRef = useRef<HTMLIonInputElement>(null);
  const { referralId, setReferralId } = useReferralStore();

  const { handleSubmit, control, watch, setValue } = useForm<{ code: string }>({
    mode: "onSubmit",
  });

  const handleForm = async (data: FieldValues) => {
    if (!verificationData.codeId || !phoneNumber) return;

    if (referralId) {
      try {
        await checkWasInvitedMutationAsync({
          inviteePhone: phoneNumber,
          referralId: referralId,
        });
        setReferralId(null);
      } catch (error: any) {
        const rawMessage =
          error && typeof error === "object" && "message" in error && typeof (error as any).message === "string"
            ? (error as any).message
            : (error as any)?.response?.data?.message || "Failed to send invitation";

        const businessErrors = new Set([
          "Missing referrer data",
          "You cannot invite yourself",
          "This phone number belongs to an existing customer",
          "An invitation for this phone number already exists",
        ]);

        if (businessErrors.has(rawMessage)) {
          setReferralId(null);
          if (rawMessage === "An invitation for this phone number already exists") {
            toast.error(t(rawMessage as keyof typeof t));
          }
        } else {
          console.log(error, "referral check error");
        }
      }
    }

    mutateAuth(
      {
        phone: phoneNumber,
        verificationCode: data.code,
        verificationCodeId: verificationData.codeId,
      },
      {
        onError: (error) => {
          console.log(error, "error");
          if (error?.response?.data?.message === "Verification code data is not valid") {
            present({
              color: "danger",
              message: t("Invalid verification code"),
              duration: 1500,
              position: "bottom",
            });
          }
        },
        onSuccess: async (data) => {
          console.log("onSuccess");
          await handleCustomerLogin(data);

          if (history.location.state?.from) {
            history.goBack();
          } else {
            history.replace("/home3");
          }
        },
      },
    );
  };

  const isSmsVerification = verificationData.method === "sms";
  const verificationLabel = isSmsVerification
    ? t("Enter the code from the SMS message sent to {{phoneNumber}}", { phoneNumber })
    : t("Please enter the last 4 digits of the incoming call number", { phoneNumber });

  const continueButtonDisabled = watch("code")?.length !== 4 || isAuthLoading || isCheckWasInvitedLoading || isSuccess;
  return (
    <form onSubmit={handleSubmit(handleForm)}>
      <Controller
        name="code"
        control={control}
        render={({ field: { onChange, value } }) => (
          <CommonFormElement isValid={!isError} label={verificationLabel} errorMessage={t("Invalid access code")}>
            <IonInput
              ref={inputRef}
              autofocus
              type="text"
              maxlength={4}
              inputMode="numeric"
              style={{
                "--padding-start": "40px",
              }}
              className="border text-center border-gray-300 rounded-md"
              value={value}
              onKeyPress={(event) => {
                if (event.key !== "Enter") {
                  validateInputToNumberType(event);
                }
              }}
              onIonChange={onChange}
            />
          </CommonFormElement>
        )}
      />
      <IonButton
        fill="clear"
        type="submit"
        disabled={continueButtonDisabled}
        className="flex bg-primary rounded-full mx-5 !text-black"
      >
        <span className="!text-black">{t("Continue")}</span>
        {isAuthLoading && <IonSpinner className="ml-2 size-5" />}
      </IonButton>
      <ResendCallCode
        className="text-black flex "
        phone={phoneNumber}
        onSendSuccess={(data) => {
          setVerificationData({
            codeId: data.verificationCodeId,
            phoneNumber: data.phoneNumber,
            method: data.method,
          });
          if (data.method === "sms") {
            inputRef.current?.setFocus();
            setValue("code", "");
          }
        }}
      />
      <div className="text-center">или</div>
      <ResendOtpSms
        phone={phoneNumber}
        onSendSuccess={(data) => {
          setVerificationData({
            codeId: data.verificationCodeId,
            phoneNumber: data.phoneNumber,
            method: "sms",
          });
          inputRef.current?.setFocus();
          setValue("code", "");
        }}
      />
    </form>
  );
};

export { OtpCodeInputForm };
